'use client'

import React, { Fragment, useEffect, useState, Suspense } from 'react'
import { Recipe, useGetUserRecipesLazyQuery } from 'graphql/generated/graphql'
import RecipeCookbookCard from '../RecipeCoobookCard'
import { SvgSpinner } from '../icons'
import Link from 'next/link'
import { PiMagnifyingGlassLight } from "react-icons/pi";
import { useRouter, useSearchParams } from 'next/navigation'
import { Spinner } from 'flowbite-react'
import RecipieCard from '../recipieCard'
import { recipeCardSrc } from '@/lib/recipeCardSrc'
import { Plus } from 'lucide-react'
import RecipeImportModal from './RecipeImportModal'
import { useBookmarkedRecipeIds } from '@/hook/useBookmarkedRecipeIds'
import { RecipesEmptyState } from './RecipesEmptyState'
import { RecipeAddSubmenu } from './RecipeAddSubmenu'
import { VisitorRecipeEmptyState } from '@/components/profile/VisitorRecipeEmptyState'

const ProfileRecipesInner = ({userID, slug, owner, listingID, userType, previewMode = false, previewLimit = 3, renderEmptyState }: {userID: number, slug: string, owner: boolean, listingID: number, userType: string, previewMode?: boolean, previewLimit?: number, renderEmptyState?: (onAdd: () => void) => React.ReactNode}) => {

    const params = useSearchParams()
    const router = useRouter()
    const { bookmarkedIds, toggleBookmark } = useBookmarkedRecipeIds()
    const [recipes, setRecipes] = useState<any>([])
    const [recipesHolder, setRecipesHolder] = useState<any>([])
    const [currentPage, setCurrentPage] = useState<number>(1)
    const [showLoadMoreButton, setShowLoadMoreButton] = useState<boolean>(true)
    const [loadingNextPage, setLoadingNextPage] = useState<boolean>(false)
    const [importModalOpen, setImportModalOpen] = useState<boolean>(false)
    const [recipeSubmenuOpen, setRecipeSubmenuOpen] = useState<boolean>(false)
    //const user = useSelector((state: any) => state.culineerAuth.user)

    const pageSize = previewMode ? previewLimit : 100
    const [getRecipeList, { fetchMore, loading }] = useGetUserRecipesLazyQuery({fetchPolicy: 'no-cache'})

    useEffect(() => {
        getRecipes()
    }, [])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (recipeSubmenuOpen) {
                setRecipeSubmenuOpen(false)
            }
        }

        if (recipeSubmenuOpen) {
            document.addEventListener('click', handleClickOutside)
        }

        return () => document.removeEventListener('click', handleClickOutside)
    }, [recipeSubmenuOpen])

    const getRecipes = async () => {
        try {

          const variables: any = {
            limit: pageSize,
            page: currentPage
          };

          if (userType?.toLowerCase() === 'general') {
            variables.user_id = userID;
          } else {
            variables.listing_id = listingID;
          }


          const response = await getRecipeList({
              variables: variables
          });
          
          // Check for GraphQL errors
          if (response.error) {
            console.error('GraphQL Error:', response.error);
            return;
          }

          // Check for errors array
          if (response.errors && response.errors.length > 0) {
            console.error('GraphQL Errors:', response.errors);
            return;
          }
          
          const result = response.data;

          if (!result) {
            console.error('No data returned from query - response.data is null/undefined');
            return;
          }

          if (!result.userRecipes) {
            console.error('No userRecipes field in response');
            console.error('Available fields:', Object.keys(result));
            return;
          }

          const recipesList = result.userRecipes.recipes || []
          setRecipes(previewMode ? recipesList.slice(0, previewLimit) : recipesList)
          setRecipesHolder(previewMode ? recipesList.slice(0, previewLimit) : recipesList)

          const totalPages = Math.ceil(Number(result.userRecipes?.total || 0) / pageSize)
  
          if(Number(result.userRecipes?.total) === 0 || currentPage === totalPages){
              setShowLoadMoreButton(false)
              return
          }
          
          setCurrentPage(Number(result.userRecipes.page) + 1)
        } catch (error) {
          console.error('Error in getRecipes:', error)
          console.error('Error stack:', error instanceof Error ? error.stack : 'No stack trace')
        }
    }

    const loadMoreRecipes = async () => {
      setLoadingNextPage(!loadingNextPage)
        const variables = {
          limit: pageSize,
          page: currentPage
        };

        if (userType?.toLowerCase() === 'general') {
          Object.assign(variables, { user_id: userID });
        } else {
          Object.assign(variables, { listing_id: listingID });
        }

        const result = (await fetchMore({
            variables: {
                ...variables,
                limit: pageSize,
                page: currentPage
            }
        })).data

        setRecipes((prev: any) => prev.concat(result.userRecipes.recipes))
        setRecipesHolder((prev: any) => prev.concat(result.userRecipes.recipes))

        const totalPages = Math.ceil(Number(result?.userRecipes?.total) / pageSize)
        
        if(Number(result?.userRecipes?.total) === 0 || currentPage === totalPages){
            setShowLoadMoreButton(false)
            return
        }
        
        setCurrentPage(Number(result?.userRecipes?.page) + 1)

        setLoadingNextPage(false)
    }

    const searchRecipes = (query: string) => {
        if(query.length === 0) {
            setRecipes(recipesHolder)
        } else {
            const filteredEvents = recipes.filter((recipe: Recipe) => {
                return recipe?.recipe_name?.toLowerCase().includes(query)
                || recipe?.author_name?.toLowerCase().includes(query) || recipe?.preparation_time === Number(query)
            })
            setRecipes(filteredEvents)
        }
    }

    const handleImportSuccess = (scrapedData: any) => {
        // Store scraped data in sessionStorage and navigate to recipe add page
        sessionStorage.setItem('importedRecipeData', JSON.stringify(scrapedData))
        router.push('/recipes/add?imported=true')
    }

    if(loading) {
        return (
            <div className='flex h-full pt-10 justify-center items-center'>
                <Spinner aria-label="loading" size="xl" color={'success'} />
            </div>
        )
    }
    
    return (
      <div className="w-full">
        {!previewMode && (
          <div className="flex sm:flex-row flex-col sm:items-center items-start justify-between pt-4 pb-4 gap-3">
            <div className="flex items-center gap-3">
              <h1 className="font-semibold">{recipes?.length || 0} Recipes</h1>
              {owner && (
                <div className="relative">
                  <button
                    type="button"
                    className="flex items-center gap-2 whitespace-nowrap rounded-full bg-primary px-3 py-1.5 text-white transition-colors hover:bg-[#75B339] sm:px-4 sm:py-2"
                    onClick={(e) => {
                      e.stopPropagation()
                      setRecipeSubmenuOpen(!recipeSubmenuOpen)
                    }}
                  >
                    <Plus className="h-4 w-4" />
                    <span className="text-sm">Create Recipe</span>
                  </button>
                  {recipeSubmenuOpen && (
                    <div
                      className="absolute top-full left-0 z-50 mt-2 min-w-[200px] rounded-lg border border-gray-200 bg-white shadow-lg"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setRecipeSubmenuOpen(false)
                          router.push('/recipes/add')
                        }}
                        className="flex w-full items-center gap-2 rounded-t-lg border-b border-gray-100 px-4 py-3 text-left text-sm hover:bg-gray-100"
                      >
                        <span>✏️</span>
                        Type it in
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setRecipeSubmenuOpen(false)
                          setImportModalOpen(true)
                        }}
                        className="flex w-full items-center gap-2 rounded-b-lg px-4 py-3 text-left text-sm hover:bg-gray-100"
                      >
                        <span>🔗</span>
                        Paste a link
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="relative xs:w-full sm:w-1/3">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">
                <PiMagnifyingGlassLight size={24} color="#85C349" />
              </div>
              <input
                name="query"
                placeholder="Search recipes"
                type="search"
                className="w-full rounded-full border border-[#CBD7DD] bg-white h-[50px] px-6 pl-9 pr-3 text-base font-medium text-[#16270C] placeholder-[#6B6B6B] placeholder:font-normal transition-all duration-100 ease-in-out placeholder:text-base focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary"
                onChange={(e) => searchRecipes(e.target.value)}
              />
            </div>
          </div>
        )}
        {previewMode && (
          <div className="pt-4 pb-4">
            <h1 className="font-semibold text-lg">Recipes</h1>
          </div>
        )}
        <div className="w-full">
          {recipes?.length !== 0 ? (
            <div>
              <div className={`grid gap-8 ${previewMode ? 'sm:grid-cols-3 xs:grid-cols-1' : 'sm:grid-cols-4 xs:grid-cols-1'}`}>
                {recipes?.map(
                  (recipe: {
                    recipe_id: React.Key | null | undefined
                    recipe_img_url: any
                    average_recipe_rating: any
                    recipe_name: string
                    resourcePath: string
                    total_time: number
                    cook_time: number
                    preparation_time: number
                    recipe_ingredients: any
                    recipe_comments: {
                      id: number
                    }[]
                    user_id: number
                    slug: string
                    user: {
                      full_name: string
                      user_id: number
                      slug: string
                      profile_photo_resource: string
                      default_listing: {
                        resource_path: string
                        photo: string
                      }
                    }
                  }) => (
                    <RecipieCard
                      key={recipe?.recipe_id}
                      link={`/recipes/${recipe.slug}`}
                      src={recipeCardSrc(recipe)}
                      numberOfIngredients={recipe?.recipe_ingredients?.length}
                      title={recipe?.recipe_name as string}
                      rating={recipe?.average_recipe_rating || 0}
                      reviewCount={recipe?.recipe_comments?.length || 0}
                      time={recipe?.total_time}
                      user={recipe?.user}
                      className={'min-h-[332px] '}
                      recipeId={recipe?.recipe_id as number}
                      isBookmarked={bookmarkedIds.has(recipe?.recipe_id as number)}
                      onToggleBookmark={!owner ? toggleBookmark : undefined}
                    />
                  )
                )}
              </div>
              {!previewMode && showLoadMoreButton && recipes?.length > 0 && (
                <div className="flex w-full items-center justify-center py-8">
                  <button
                    onClick={loadMoreRecipes}
                    className={`flex items-center justify-center rounded-full border border-[#85C349] bg-white py-2 px-4 text-center text-sm font-semibold text-[#85C349] ${
                      loadingNextPage ? 'cursor-not-allowed opacity-50' : ''
                    }`}
                    disabled={loadingNextPage}
                  >
                    {loadingNextPage ? <SvgSpinner /> : 'Load More'}
                  </button>
                </div>
              )}
            </div>
          ) : (
            (renderEmptyState ? renderEmptyState(() => setRecipeSubmenuOpen(!recipeSubmenuOpen)) : owner ? (
              <RecipesEmptyState
                message="Looks like you haven't added any recipes yet"
                action={<RecipeAddSubmenu />}
              />
            ) : (
              <VisitorRecipeEmptyState
                userProfile={{
                  user_id: userID,
                  slug,
                  user_type: userType,
                }}
              />
            ))
          )}
        </div>

        {/* Recipe Import Modal */}
        <RecipeImportModal
          open={importModalOpen}
          onClose={() => setImportModalOpen(false)}
          onSuccess={handleImportSuccess}
        />
      </div>
    )
}

const ProfileRecipes = (props: {userID: number, slug: string, owner: boolean, listingID: number, userType: string, previewMode?: boolean, previewLimit?: number, renderEmptyState?: (onAdd: () => void) => React.ReactNode}) => (
  <Suspense fallback={null}>
    <ProfileRecipesInner {...props} />
  </Suspense>
)

export default ProfileRecipes